import React, { useRef, useCallback, memo, useState } from 'react';
import {
  Box,
  Image,
  Text,
  Flex,
  Badge,
  IconButton,
  useDisclosure,
  useOutsideClick,
  useToast,
  HStack,
  Avatar,
  AvatarGroup,
  VisuallyHidden,
} from '@chakra-ui/react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Share2, MoreVertical, Edit, Trash, Copy, UserMinus, Globe, Bookmark, Pin, TrendingUp, Sparkles, RefreshCw, Lock, Camera } from 'lucide-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useSelector } from 'react-redux';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { getDisplayName } from '@/types/user';
import { getRandomGradient, getListInitials } from '@/utils/listUtils';
import { useFollowList, useUnfollowList, useUpdateCoverArt, usePinList, useUnpinList } from '@/api';
import { getPinErrorToast } from '@/api/mutations/pinErrorMessage';
import { useCloudinaryUpload } from '@/hooks/useCloudinaryUpload';
import { RestaurantListOverview } from '@/types/lists/restaurantList';
import { selectIsLoggedIn } from '@/store/slices/authSlice';
import {
  RenameListModal,
  DeleteListModal,
  CopyListModal,
} from '@/modals/lists';
import { ShareListModal } from '@/modals/share';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

// Motion components
const MotionBox = motion.create(Box);
const MotionBadge = motion.create(Badge);

// Animation variants - optimized for iOS performance
const cardVariants: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: Math.min(i * 0.03, 0.15), // Cap delay at 150ms for faster perceived load
      duration: 0.2,
      ease: 'easeOut',
    },
  }),
};

const pulseVariants: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut',
    },
  },
};

const bookmarkVariants: Variants = {
  tap: {
    scale: [1, 1.3, 0.9, 1.1, 1],
    transition: { duration: 0.4 },
  },
};

interface ListPreviewCardProps {
  list: RestaurantListOverview;
  index?: number;
  onLongPress?: (list: RestaurantListOverview) => void;
  onSignUpClick?: (context: 'save' | 'copy') => void;
}

export const ListPreviewCard = memo(function ListPreviewCard({ list, index = 0, onLongPress, onSignUpClick }: ListPreviewCardProps) {
  const navigate = useNavigate();
  const { tab, q } = useSearch({ strict: false }) as { tab?: string; q?: string };
  const toast = useToast();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);
  const [coverArtError, setCoverArtError] = useState(false);

  // API mutations
  const { mutateAsync: followList, isPending: isFollowing } = useFollowList();
  const { mutateAsync: unfollowList, isPending: isUnfollowing } = useUnfollowList();
  const { mutateAsync: updateCoverArt } = useUpdateCoverArt();
  const pinList = usePinList();
  const unpinList = useUnpinList();

  const onCoverUploadSuccess = useCallback(
    (result: { url: string }) => {
      updateCoverArt({ urlKey: getKeyFromUrl(list.urlKey), coverArtUrl: result.url });
    },
    [updateCoverArt, list.urlKey],
  );

  const { openUploadWidget: openCoverUpload } = useCloudinaryUpload({
    mode: 'widget',
    cropping: true,
    aspectRatio: 2,
    onSuccess: onCoverUploadSuccess,
  });

  // Modal disclosure hooks
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isCopyOpen, onOpen: onCopyOpen, onClose: onCopyClose } = useDisclosure();

  // Custom dropdown menu — avoids Popper.js positioning issues with transform: translate3d()
  const { isOpen: isMenuOpen, onToggle: onMenuToggle, onClose: onMenuClose } = useDisclosure();
  const menuRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: menuRef, handler: onMenuClose });

  const getKeyFromUrl = (url: string): string => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const handleTap = useCallback(async () => {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {
      // Haptics not available on web
    }
  }, []);

  const handleToggleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!list.urlKey) return;

    if (!isLoggedIn) {
      onSignUpClick?.('save');
      return;
    }

    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch {
      // Haptics not available on web
    }

    try {
      if (list.isFollowed) {
        await unfollowList(getKeyFromUrl(list.urlKey));
        toast({
          title: 'Unfollowed list',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await followList(getKeyFromUrl(list.urlKey));
        toast({
          title: 'Following list',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      toast({
        title: `Failed to ${list.isFollowed ? 'unfollow' : 'follow'} list`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleLongPressStart = useCallback(() => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(async () => {
      isLongPress.current = true;
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch {
        // Haptics not available on web
      }
      onLongPress?.(list);
    }, 500);
  }, [list, onLongPress]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleCardClick = useCallback(() => {
    if (!isLongPress.current) {
      handleTap();
      // Track list click
      analytics.capture(AnalyticsEvents.LIST_CLICKED, {
        list_id: list.id,
        list_url_key: list.urlKey,
        list_name: list.name,
        list_type: list.isOwned ? 'personal' : list.isFollowed ? 'following' : 'community',
        is_collaborative: list.isCollaborative || false,
      });
      navigate({
        to: '/lists/$urlKey',
        params: { urlKey: list.urlKey },
        search: q ? { q } : undefined,
      });
    }
    isLongPress.current = false;
  }, [handleTap, navigate, list, tab, q]);

  // Preview images (first 3 restaurant images)
  const previewImages = list.previewImages?.slice(0, 3) || [];

  // Top-left badge cluster: priority order determines which 2 show before overflowing.
  // Pinned outranks the rest — it's the member's own durable signal about their own
  // list, vs. Trending/Updated which are system-derived and transient.
  const badgeIds = [
    list.isPinnedToProfile && 'pinned',
    list.isNew && 'new',
    list.isPersonal && list.isPublic && 'public',
    list.isTrending && 'trending',
    list.isRecentlyUpdated && 'updated',
  ].filter((id): id is string => !!id);
  const visibleBadgeIds = new Set(badgeIds.slice(0, 2));
  const overflowBadgeIds = badgeIds.slice(visibleBadgeIds.size);
  const overflowBadgeCount = overflowBadgeIds.length;
  const badgeLabels: Record<string, string> = {
    pinned: 'Pinned',
    new: 'New',
    public: 'Public',
    trending: 'Trending',
    updated: 'Updated',
  };
  const overflowBadgeLabel = `Also: ${overflowBadgeIds.map((id) => badgeLabels[id]).join(', ')}`;

  return (
    <>
      <MotionBox
        custom={index}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{ y: -8, scale: 1.02, boxShadow: '0 20px 40px var(--color-overlay-15)' }}
        whileTap={{ scale: 0.98 }}
        bg="var(--card-bg)"
        borderRadius="var(--r-card)"
        cursor="pointer"
        position="relative"
        boxShadow="var(--card-shadow), 0 4px 20px var(--color-overlay-08)"
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        onClick={handleCardClick}
      >
        {/* Hero Image Section */}
        <Box height={{ base: '140px', lg: '180px' }} position="relative" overflow="hidden" borderTopRadius="var(--r-card)" bg="var(--photo-placeholder)">
          {list.coverArt && !coverArtError ? (
            <Image
              src={list.coverArt}
              alt={list.name}
              objectFit="cover"
              width="100%"
              height="100%"
              loading="lazy"
              transition="transform 0.3s ease"
              _groupHover={{ transform: 'scale(1.05)' }}
              onError={() => setCoverArtError(true)}
            />
          ) : (
            <Box
              width="100%"
              height="100%"
              bgGradient={getRandomGradient(list.urlKey, list.name)}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text
                fontSize="4xl"
                fontWeight="bold"
                color="white"
                textShadow="0 2px 8px var(--color-overlay-30)"
              >
                {getListInitials(list.name)}
              </Text>
            </Box>
          )}

          {/* Gradient Overlay */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            height="60%"
            bgGradient="linear(to-t, blackAlpha.700 0%, transparent 100%)"
            pointerEvents="none"
          />

          {/* Top Left Badges (Pinned/New/Public/Trending/Updated — capped at 2 + overflow) */}
          <HStack position="absolute" top="3" left="3" spacing={2}>
            {visibleBadgeIds.has('pinned') && (
              <Badge
                bg="gray.900"
                color="white"
                p={1.5}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 2px 8px var(--color-overlay-20)"
              >
                <Pin size={12} aria-hidden="true" />
                <VisuallyHidden>Pinned to profile</VisuallyHidden>
              </Badge>
            )}

            <AnimatePresence>
              {visibleBadgeIds.has('new') && (
                <MotionBadge
                  variants={pulseVariants}
                  animate="animate"
                  bg="green.500"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  boxShadow="0 2px 8px var(--color-overlay-20)"
                >
                  <Sparkles size={12} />
                  New
                </MotionBadge>
              )}
            </AnimatePresence>

            {visibleBadgeIds.has('public') && (
              <Badge
                bg="var(--color-purple-glow-95)"
                color="white"
                px={2}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
                boxShadow="0 2px 8px var(--color-overlay-20)"
              >
                <Globe size={12} />
                Public
              </Badge>
            )}

            {visibleBadgeIds.has('trending') && (
              <Badge
                bg="orange.500"
                color="white"
                px={2}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
                boxShadow="0 2px 8px var(--color-overlay-20)"
              >
                <TrendingUp size={12} />
                Trending
              </Badge>
            )}

            {visibleBadgeIds.has('updated') && (
              <Badge
                bg="blue.500"
                color="white"
                px={2}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="bold"
                display="flex"
                alignItems="center"
                gap={1}
                boxShadow="0 2px 8px var(--color-overlay-20)"
              >
                <RefreshCw size={12} />
                Updated
              </Badge>
            )}

            {overflowBadgeCount > 0 && (
              <Badge
                bg="blackAlpha.700"
                color="white"
                px={2}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="bold"
                boxShadow="0 2px 8px var(--color-overlay-20)"
              >
                <Text aria-hidden="true">+{overflowBadgeCount}</Text>
                <VisuallyHidden>{overflowBadgeLabel}</VisuallyHidden>
              </Badge>
            )}
          </HStack>

          {/* Bottom Left Preview Thumbnails */}
          {previewImages.length > 0 && (
            <HStack
              position="absolute"
              bottom="3"
              left="3"
              spacing={-3}
            >
              <AvatarGroup size="md" max={3} spacing={-3}>
                {previewImages.map((img, idx) => (
                  <Avatar
                    key={idx}
                    src={img.url}
                    name={img.restaurantName}
                    size="md"
                    border="2px solid white"
                    boxShadow="0 2px 8px var(--color-overlay-30)"
                  />
                ))}
              </AvatarGroup>
              {list.restaurantCount > 3 && (
                <Badge
                  ml={2}
                  bg="blackAlpha.700"
                  color="white"
                  fontSize="xs"
                  borderRadius="full"
                  px={2}
                  py={1}
                >
                  +{list.restaurantCount - 3}
                </Badge>
              )}
            </HStack>
          )}

          {/* Bottom Right Follow Button */}
          {!list.isPersonal && (
            <MotionBox
              position="absolute"
              bottom="3"
              right="3"
              variants={bookmarkVariants}
              whileTap="tap"
              onClick={handleToggleFollow}
            >
              <IconButton
                aria-label={list.isFollowed ? 'Unfollow list' : 'Follow list'}
                icon={
                  <Bookmark
                    size={20}
                    fill={list.isFollowed ? 'currentColor' : 'none'}
                  />
                }
                size="sm"
                borderRadius="full"
                bg={list.isFollowed ? 'purple.500' : 'whiteAlpha.900'}
                color={list.isFollowed ? 'white' : 'gray.700'}
                _hover={{
                  bg: list.isFollowed ? 'purple.600' : 'white',
                  transform: 'scale(1.1)',
                }}
                boxShadow="0 2px 8px var(--color-overlay-20)"
                isLoading={isFollowing || isUnfollowing}
              />
            </MotionBox>
          )}
        </Box>

        {/* Top Right Menu — custom dropdown; avoids Popper.js transform: translate3d() misalignment */}
        <Box
          ref={menuRef}
          position="absolute"
          top="2"
          right="3"
          zIndex={1000}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Inner wrapper is the CSS offset parent for the dropdown's right:0 */}
          <Box position="relative">
          <IconButton
            aria-label="List options"
            icon={<MoreVertical size={18} />}
            variant="solid"
            bg="var(--color-overlay-50)"
            color="white"
            size="sm"
            borderRadius="full"
            _hover={{ bg: 'var(--color-overlay-70)' }}
            _active={{ bg: 'var(--color-overlay-80)' }}
            onClick={onMenuToggle}
          />

          {isMenuOpen && (
            <Box
              position="absolute"
              top="100%"
              right={0}
              mt={1}
              bg="gray.900"
              border="1px solid"
              borderColor="gray.700"
              borderRadius="var(--r-card-compact)"
              zIndex={1000}
              minW="185px"
              boxShadow="lg"
              py={1}
            >
              {/* Share — always shown */}
              <Flex
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'gray.800' }}
                align="center"
                gap={2}
                fontSize="sm"
                color="white"
                borderRadius="var(--r-chip)"
                onClick={() => { onMenuClose(); onShareOpen(); }}
              >
                <Share2 size={16} />
                Share
              </Flex>

              {list.isPersonal ? (
                <>
                  <Flex
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="white"
                    borderRadius="var(--r-chip)"
                    onClick={() => { onMenuClose(); onRenameOpen(); }}
                  >
                    <Edit size={16} />
                    Rename
                  </Flex>
                  <Flex
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="white"
                    borderRadius="var(--r-chip)"
                    onClick={() => { onMenuClose(); openCoverUpload(); }}
                  >
                    <Camera size={16} />
                    Update Cover
                  </Flex>
                  {/* Pin / Unpin to profile */}
                  <Flex
                    px={3}
                    py={2}
                    cursor={pinList.isPending || unpinList.isPending ? 'not-allowed' : 'pointer'}
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="white"
                    borderRadius="var(--r-chip)"
                    opacity={pinList.isPending || unpinList.isPending ? 0.6 : 1}
                    onClick={() => {
                      if (pinList.isPending || unpinList.isPending) return;
                      onMenuClose();
                      if (list.isPinnedToProfile) {
                        unpinList.mutate(
                          { urlKey: list.urlKey },
                          {
                            onError: (error) => {
                              const { title, status, id } = getPinErrorToast(error);
                              if (!toast.isActive(id)) {
                                toast({ id, title, status, duration: null, isClosable: true });
                              }
                            },
                          }
                        );
                      } else {
                        pinList.mutate(
                          { urlKey: list.urlKey },
                          {
                            onError: (error) => {
                              const { title, status, id } = getPinErrorToast(error);
                              if (!toast.isActive(id)) {
                                toast({ id, title, status, duration: null, isClosable: true });
                              }
                            },
                          }
                        );
                      }
                    }}
                  >
                    <Pin
                      size={16}
                      fill={list.isPinnedToProfile ? 'currentColor' : 'none'}
                    />
                    {list.isPinnedToProfile ? 'Unpin from Profile' : 'Pin to Profile'}
                  </Flex>
                  <Flex
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="red.300"
                    borderRadius="var(--r-chip)"
                    onClick={() => { onMenuClose(); onDeleteOpen(); }}
                  >
                    <Trash size={16} />
                    Delete
                  </Flex>
                </>
              ) : (
                <>
                  <Flex
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="white"
                    borderRadius="var(--r-chip)"
                    onClick={(e) => { onMenuClose(); handleToggleFollow(e); }}
                  >
                    <Bookmark
                      size={16}
                      fill={list.isFollowed ? 'currentColor' : 'none'}
                    />
                    {list.isFollowed ? 'Unfollow' : 'Follow'}
                  </Flex>
                  <Flex
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.800' }}
                    align="center"
                    gap={2}
                    fontSize="sm"
                    color="white"
                    borderRadius="var(--r-chip)"
                    onClick={() => {
                      onMenuClose();
                      if (!isLoggedIn) {
                        onSignUpClick?.('copy');
                        return;
                      }
                      onCopyOpen();
                    }}
                  >
                    {isLoggedIn ? <Copy size={16} /> : <Lock size={16} />}
                    Copy
                  </Flex>
                </>
              )}
            </Box>
          )}
          </Box>
        </Box>

        {/* Card Content */}
        <Box p={{ base: 3, lg: 4 }}>
          {/* Title */}
          <Text
            fontWeight="bold"
            fontSize={{ base: 'md', lg: 'lg' }}
            color="var(--card-text)"
            noOfLines={1}
            mb={1}
          >
            {list.name}
          </Text>

          {/* Creator info */}
          {list.user && !list.isPersonal && (
            <Text fontSize="sm" color="var(--card-text-2)" mb={2}>
              by {getDisplayName(list.user)}
            </Text>
          )}

          {/* Stats row */}
          <Flex justify="space-between" align="center">
            <HStack spacing={3} color="var(--card-text-2)" fontSize="sm">
              <Text>
                {list.matchingRestaurantCount != null && list.matchingRestaurantCount < list.restaurantCount
                  ? `${list.matchingRestaurantCount} of ${list.restaurantCount} spots match`
                  : `${list.restaurantCount} ${list.restaurantCount === 1 ? 'spot' : 'spots'}`}
              </Text>
              {list.followerCount > 0 && (
                <Text>
                  {list.followerCount} {list.followerCount === 1 ? 'follower' : 'followers'}
                </Text>
              )}
            </HStack>

            {/* Tags */}
            {list.tags && list.tags.length > 0 && (
              <HStack spacing={1} flexWrap="wrap">
                {list.tags.slice(0, 2).map((tag) => (
                  <Badge
                    key={tag.id}
                    size="sm"
                    bg="purple.100"
                    color="purple.700"
                    variant="subtle"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="xs"
                  >
                    {tag.icon} {tag.name}
                  </Badge>
                ))}
                {list.tags.length > 2 && (
                  <Badge
                    size="sm"
                    bg="var(--card-surface-2)"
                    color="var(--card-text-2)"
                    variant="subtle"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="xs"
                  >
                    +{list.tags.length - 2}
                  </Badge>
                )}
              </HStack>
            )}
          </Flex>
        </Box>
      </MotionBox>

      {/* Modals */}
      <RenameListModal
        isOpen={isRenameOpen}
        onClose={onRenameClose}
        listName={list.name}
        urlKey={list.urlKey}
      />

      <DeleteListModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        listName={list.name}
        urlKey={list.urlKey}
      />

      <ShareListModal
        listType="personal"
        isOpen={isShareOpen}
        onClose={onShareClose}
        urlKey={list.urlKey}
        listName={list.name}
      />

      <CopyListModal
        isOpen={isCopyOpen}
        onClose={onCopyClose}
        listName={list.name}
        urlKey={list.urlKey}
      />
    </>
  );
});

export default ListPreviewCard;
